import * as React from 'react';
import {Component, Expressions} from '@packages/systems/wfdl';
import type {Expression} from '@packages/systems/wfdl-ast';
import {
  getUserCustomizedElementLabel,
  usePlugins,
} from '@packages/utilities/platform';
import {truncateMid} from '@packages/utilities/string-utils';
import {
  selectComponents,
  selectCurrentComponent,
} from '@packages/utilities/platform-selectors';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';

const selectAssetState = (state: DesignerStores) => state.AssetStore;
const selectStyleBlockState = (state: DesignerStores) => state.StyleBlockStore;

export const getOverlayTargetSummaryLabel = ({
  currentComponentRender,
  components,
  plugins,
  styleBlockState,
  assetState,
  targetId,
  fallbackLabel,
}: {
  currentComponentRender: Expression;
  components: Component.ComponentMap;
  plugins: ReturnType<typeof usePlugins>;
  styleBlockState: DesignerStores['StyleBlockStore'];
  assetState: DesignerStores['AssetStore'];
  targetId: string | undefined;
  fallbackLabel: string;
}): string => {
  if (!targetId) return fallbackLabel;

  const target = Expressions.getElementById(currentComponentRender, targetId);
  if (!target) return fallbackLabel;

  return getUserCustomizedElementLabel(
    target,
    currentComponentRender,
    components,
    plugins,
    styleBlockState,
    assetState
  );
};

export const LinkOverlaySummary = ({
  targetId,
  fallbackLabel,
}: {
  targetId: string | undefined;
  fallbackLabel: string;
}) => {
  const components = useDesignerSelector(selectComponents);
  const currentComponent = useDesignerSelector(selectCurrentComponent);
  const plugins = usePlugins();
  const styleBlockState = useDesignerSelector(selectStyleBlockState);
  const assetState = useDesignerSelector(selectAssetState);

  const label = React.useMemo(
    () =>
      getOverlayTargetSummaryLabel({
        currentComponentRender: Component.getRender(currentComponent),
        components,
        plugins,
        styleBlockState,
        assetState,
        targetId,
        fallbackLabel,
      }),
    [
      assetState,
      components,
      currentComponent,
      fallbackLabel,
      plugins,
      styleBlockState,
      targetId,
    ]
  );

  return <span>{truncateMid(label, 30)}</span>;
};
